export interface TabSessionCreationLease {
    /** False once the tab request was invalidated, replaced, or globally reset. */
    isCurrent(): boolean;
}

/**
 * Owns per-tab session-creation single-flight state.
 *
 * Invalidating a tab revokes the lease held by its current async task. The task
 * may still finish its RPC, but callers can then close the orphaned session
 * instead of linking stale state back to a renamed or removed tab.
 */
export class TabSessionCreationController {
    private readonly pending = new Map<string, Promise<string>>();
    private readonly generations = new Map<string, number>();
    private epoch = 0;

    get(tabId: string): Promise<string> | undefined {
        return this.pending.get(tabId);
    }

    has(tabId: string): boolean {
        return this.pending.has(tabId);
    }

    run(
        tabId: string,
        task: (lease: TabSessionCreationLease) => Promise<string>,
    ): Promise<string> {
        const existing = this.pending.get(tabId);
        if (existing) return existing;

        const epoch = this.epoch;
        const generation = (this.generations.get(tabId) ?? 0) + 1;
        this.generations.set(tabId, generation);
        const lease: TabSessionCreationLease = {
            isCurrent: () =>
                this.epoch === epoch &&
                this.generations.get(tabId) === generation,
        };

        let taskPromise: Promise<string>;
        try {
            taskPromise = task(lease);
        } catch (error) {
            taskPromise = Promise.reject(error);
        }

        let trackedPromise: Promise<string>;
        trackedPromise = taskPromise.finally(() => {
            if (this.pending.get(tabId) === trackedPromise) {
                this.pending.delete(tabId);
            }
        });
        this.pending.set(tabId, trackedPromise);
        return trackedPromise;
    }

    invalidate(tabId: string): void {
        this.generations.set(tabId, (this.generations.get(tabId) ?? 0) + 1);
        this.pending.delete(tabId);
    }

    clear(): void {
        this.epoch += 1;
        this.pending.clear();
        this.generations.clear();
    }
}
